import { useContext, useEffect } from "react";
import { useParams } from "react-router-dom";
import AdminContext from "./admin-context";
import { parseBusinessSpace } from "../constants";

/**
 * Syncs the {businessSpace}/{nodeId} URL path segments into admin context on mount and whenever they
 * change. This is the single entry point for resolving a page's business space and node from the URL —
 * covering deep links and refreshes, where no dropdown change event fires. The business space is parsed
 * through {@link parseBusinessSpace} so an unknown or legacy URL value resolves to unselected rather than
 * polluting the typed context value.
 *
 * @param coerceMissingNodeToEmpty when true, a missing nodeId segment syncs as "" instead of being
 *   skipped — used by routes that can render without a node (e.g. the home/business-space-only route),
 *   so clearing the node on a business-space change is not stomped back by a stale value.
 */
export function useRouteBusinessSpaceSync(coerceMissingNodeToEmpty = false): void {
  const { nodeId, businessSpace } = useParams<{ nodeId: string; businessSpace: string }>();
  const { selectedNode, selectedBusinessSpace, changeSelectedNode, changeSelectedBusinessSpace } =
    useContext(AdminContext);

  useEffect(() => {
    const urlNodeId = coerceMissingNodeToEmpty ? nodeId ?? "" : nodeId;
    if (urlNodeId !== selectedNode) {
      changeSelectedNode(urlNodeId);
    }
    if (businessSpace && businessSpace !== selectedBusinessSpace) {
      changeSelectedBusinessSpace(parseBusinessSpace(businessSpace));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeId, businessSpace]);
}
